/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useMemo, useRef } from "react";
import styles from "./LidarBottomScatter.module.less";
import DeckGL, {
  DeckGLRef,
  OrthographicView,
  ScatterplotLayer,
  MapViewState,
  PickingInfo,
  Viewport,
} from "deck.gl";
import {
  convertInchToUserPreferenceToFixed,
  getUnit,
} from "~/helpers/userPreference";
import { UNITS } from "~/constants/unit";
import { LidarDataVisualizationZone } from "../../../../../../types/getLiDARDataVisualizationSummariesForLastSpecificHeats";

interface LidarBottomScatterProps {
  visualizationZoneData: LidarDataVisualizationZone[];
  isExpanded: boolean;
}

const LidarBottomScatter: React.FC<LidarBottomScatterProps> = ({
  visualizationZoneData,
  isExpanded,
}) => {
  const deckRef = useRef<DeckGLRef | null>(null);

  const scaleX = isExpanded ? 60 : 22;
  const scaleY = isExpanded ? 60 : 22;
  const radius = isExpanded ? 3 : 1;
  const target = isExpanded ? [0, 50] : [0, 10];

  const views = useMemo(
    () =>
      new OrthographicView({
        flipY: true,
        controller: true,
      }),
    [],
  );

  const initialLayer = useMemo(
    () => ({
      id: "scatter-plot-bottom",
      getPosition: (d: LidarDataVisualizationZone) =>
        [-d.x * scaleY, -d.y * scaleX] as [number, number],
      getRadius: () => radius,
      updateTriggers: {
        getRadius: [radius],
      },
      getFillColor: (d: LidarDataVisualizationZone) =>
        d.color as [number, number, number],
      opacity: 0.8,
      pickable: true,
      filled: true,
      radiusMinPixels: 1,
      radiusMaxPixels: 5,
    }),
    [],
  );

  const initialViewState = useMemo(
    () =>
      ({
        target,
        zoom: 0,
        minZoom: 0,
        maxZoom: 0,
        latitude: 0,
        longitude: 0,
      }) as MapViewState,
    [],
  );

  useEffect(() => {
    deckRef?.current?.deck?.setProps({
      layers: [
        new ScatterplotLayer({
          data: visualizationZoneData ?? [],
          ...initialLayer,
        }),
      ],
      initialViewState,
    });
  }, [visualizationZoneData.length]);

  const getTooltip = useCallback((info: PickingInfo) => {
    if (!info.object) {
      return null;
    }

    const { height } = info.viewport as Viewport;
    const isBottomSide = info.y > height / 2;

    const thickness = convertInchToUserPreferenceToFixed(
      info.object.thickness,
      1,
    );

    return (
      info.object?.quadrant && {
        html: `<div style="display: flex; flex-direction:column; align-items: center; gap: 0; text-shadow: rgb(0, 0, 0) 3px 0px 0px, rgb(0, 0, 0) 2.83487px 0.981584px 0px,
                        rgb(0, 0, 0) 2.35766px 1.85511px 0px, rgb(0, 0, 0) 1.62091px 2.52441px 0px,
                        rgb(0, 0, 0) 0.705713px 2.91581px 0px,
                        rgb(0, 0, 0) -0.287171px 2.98622px 0px,
                        rgb(0, 0, 0) -1.24844px 2.72789px 0px,
                        rgb(0, 0, 0) -2.07227px 2.16926px 0px,
                        rgb(0, 0, 0) -2.66798px 1.37182px 0px,
                        rgb(0, 0, 0) -2.96998px 0.42336px 0px,
                        rgb(0, 0, 0) -2.94502px -0.571704px 0px,
                        rgb(0, 0, 0) -2.59586px -1.50383px 0px,
                        rgb(0, 0, 0) -1.96093px -2.27041px 0px,
                        rgb(0, 0, 0) -1.11013px -2.78704px 0px,
                        rgb(0, 0, 0) -0.137119px -2.99686px 0px,
                        rgb(0, 0, 0) 0.850987px -2.87677px 0px,
                        rgb(0, 0, 0) 1.74541px -2.43999px 0px,
                        rgb(0, 0, 0) 2.44769px -1.73459px 0px,
                        rgb(0, 0, 0) 2.88051px -0.838247px 0px;
              "><div>${thickness}${getUnit(
                UNITS.MILLIMETER,
              )}</div><div style="font-size: 14px">Course: ${
                info.object.course
              }</div> <div style="font-size: 14px">Clock: ${
                info.object.quadrant
              }</div></div>`,
        style: {
          transform: `translate(${info.x - 65}px,${
            info.y + (isBottomSide ? -102 * (isExpanded ? 1.3 : 1) : 0)
          }px)`,
          fontSize: "24px",
          fontWeight: "600",
          color: "white",
          background: "transparent",
        },
      }
    );
  }, []);

  return (
    <div className={styles.lidarBottomScatter}>
      <DeckGL
        ref={deckRef}
        getCursor={() => "crosshair"}
        controller={{
          scrollZoom: false,
          dragPan: false,
          dragRotate: false,
          doubleClickZoom: false,
        }}
        getTooltip={getTooltip}
        views={views}
      />
    </div>
  );
};

export default LidarBottomScatter;
